import { ScopeType } from './custom-default-data.model';
import { BaseFieldRule } from './field-rule.model';
import { FieldType } from './field-type-definition.model';
import { ObjectState } from './object-state.model';

export class FieldMode {
  display: string;
  edit: string;

  static empty() {
    return new FieldMode();
  }

  static isEmpty(mode: FieldMode) {
    return JSON.stringify(FieldMode.empty()) === JSON.stringify(mode);
  }
}

export class CustomDataAdditionalDataModel {
  dependencies: CustomDataDependencyModel[];
  displayFields: string[];
  mappingKey: string;
  overrideBase: boolean;
  templateBody: string;
  tag: string;
  mode: FieldMode;
  showCopyButton: boolean;
  implementation?: string;
  incrementValues?: { [key: string]: string };
  objectStateOverrides?: { [key: string]: ObjectStateOverrideModel };

  constructor() {
    this.dependencies = [];
    this.displayFields = [];
    this.mappingKey = null;
    this.templateBody = null;
    this.overrideBase = false;
    this.mode = FieldMode.empty();
    this.showCopyButton = false;
    this.implementation = '';
  }

  static empty() {
    return new CustomDataAdditionalDataModel();
  }

  static isEmpty(additionalData: CustomDataAdditionalDataModel) {
    return (
      JSON.stringify(additionalData) ===
      JSON.stringify(CustomDataAdditionalDataModel.empty())
    );
  }
}

export class FieldDefinitionModel {
  description: string;
  fieldRule: BaseFieldRule;
  defaultValue: string;
  additionalData: string;
  automation: string;
  groupName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sortOrder: any;
  isReadOnly: boolean;
  visibility: FieldVisibility;
  allowEmpty: boolean;
  isRequired: boolean;
  isUnique: boolean;
  name: string;
  systemCode: string;
  dataObjectId: number;
  fieldTypeDefinitionId: number;
  scope: ScopeType;
  fieldType: FieldType;
  fieldCode: string;
  dataObjectDefinitionModel: unknown;
  formatting?: object;
  collectionConfig?: any;
  additionalDataModel = CustomDataAdditionalDataModel.empty();
  automationModel = AutomationModel.empty();

  static getAdditionalData(field: FieldDefinitionModel) {
    if (!field?.additionalData) {
      return null;
    }

    try {
      return JSON.parse(field.additionalData) as CustomDataAdditionalDataModel;
    } catch (error) {
      return null;
    }
  }

  static getMappingKey(field: FieldDefinitionModel) {
    const additionalData = FieldDefinitionModel.getAdditionalData(field);
    return additionalData ? additionalData.mappingKey : null;
  }

  static getMappingKeySystemCode(field: FieldDefinitionModel) {
    const mappingKey = FieldDefinitionModel.getMappingKey(field);
    return mappingKey.split('_')[1];
  }

  static getMappingKeyForeignKey(field: FieldDefinitionModel) {
    const mappingKey = FieldDefinitionModel.getMappingKey(field);
    const mappingKeyObjectName =
      FieldDefinitionModel.getMappingKeySystemCode(field);

    const foreignKey = mappingKey.split(`${mappingKeyObjectName}_`).pop();

    if (!foreignKey.startsWith('__')) {
      return foreignKey;
    }

    const originalCode = foreignKey.split('_').pop();
    // converts __fieldName to __FieldName
    return '__' + originalCode.charAt(0).toUpperCase() + originalCode.slice(1);
  }

  static isValid(field: FieldDefinitionModel) {
    if (!field.fieldTypeDefinitionId) {
      return false;
    }

    if (!field.systemCode || field.systemCode.trim().length === 0) {
      return false;
    }

    if (!field.name || field.name.trim().length === 0) {
      return false;
    }

    if (
      field.fieldRule['digits'] !== undefined &&
      field.fieldRule['digits'] !== null
    ) {
      if (field.fieldRule['digits'] < 0 || field.fieldRule['digits'] > 6) {
        return false;
      }
    }

    return true;
  }

  static isReadOnlyByObjectState(
    field: FieldDefinitionModel,
    objectState: ObjectState
  ): boolean {
    if (objectState == null) {
      return false;
    }
    const additionalData = FieldDefinitionModel.getAdditionalData(field);
    if (!additionalData?.objectStateOverrides) {
      return false;
    }
    const stateKey = ObjectState[objectState]?.toLowerCase();
    if (!stateKey) {
      return false;
    }
    return additionalData.objectStateOverrides[stateKey]?.isReadOnly === true;
  }
}

export enum FieldVisibility {
  Public = 0,
  Private = 1,
  Hidden = 2,
}

export class CustomDataDependencyModel {
  dependencyCode: string;
  mappingKey: string;
  autoUpdateDependency: boolean;
  clearDependencyChanged: boolean;

  constructor() {
    this.dependencyCode = null;
    this.mappingKey = null;
    this.autoUpdateDependency = null;
    this.clearDependencyChanged = null;
  }
}

export class AutomationModel {
  onCreate: string;
  onDelete: string;
  onUpdate: string;

  constructor() {
    this.onCreate = null;
    this.onDelete = null;
    this.onUpdate = null;
  }

  static empty() {
    return new AutomationModel();
  }
}

export interface ObjectStateOverrideModel {
  isReadOnly: boolean;
}
